import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { GatewayTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tickets/models/outputs/gateway-ticket.model';
import { CreateTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/inputs/create-ticket.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdateTicketMutation } from '@service/cpms-bo-ticket.service';

import { UpdateTicketContextProvider } from '../ticket.context';
import { TicketUpdateFormController } from './ticket-update-form/ticket-update-form.controller';

interface TicketUpdateControllerProps {
  ticket: GatewayTicketModel;
  onCancel: () => void;
  onSuccess: () => void;
}

export const TicketUpdateController = ({
  ticket,
  onCancel,
  onSuccess,
}: TicketUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateTicket] = useCpmsBoUpdateTicketMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateTicketModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.ticket-update.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<CreateTicketModel> = (
    fieldValues,
    _,
    dirtyFields,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    const dirty = buildDirtyObject(dirtyFields, fieldValues);

    if (!dirty.title && !dirty.description && dirty.assigneeId === undefined) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    updateTicket({
      id: ticket.id,
      ...dirty,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.ticket-update.success'));
        onSuccess();
      })
      .catch((error) => {
        Logger.error(error);
        toast.error(t('$.ticket-update.error', { error: t('global.unknown') }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const defaultValues = {
    id: ticket.id,
    title: ticket.title,
    description: ticket.description,
    assigneeId: ticket.assignee?.id,
  };

  return (
    <UpdateTicketContextProvider data={defaultValues}>
      <TicketUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateTicketContextProvider>
  );
};
